import * as React from "react"
import { format, isValid, parseISO } from "date-fns"
import { CalendarIcon, ChevronDown } from "lucide-react"
import type { DateRange } from "react-day-picker"

import { cn } from "../../lib/utils"
import { Calendar } from "./calendar"
import { fieldControlClassName } from "./input"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"

const ISO_DATE_FORMAT = "yyyy-MM-dd"
const DISPLAY_DATE_FORMAT = "dd MMM yyyy"

function parseIsoDate(value: string): Date | undefined {
  if (!value) return undefined
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : undefined
}

function toIsoDate(value?: Date): string {
  return value ? format(value, ISO_DATE_FORMAT) : ""
}

export function dateRangeStringsToRange(from: string, to: string): DateRange | undefined {
  const rangeFrom = parseIsoDate(from)
  const rangeTo = parseIsoDate(to)
  if (!rangeFrom && !rangeTo) return undefined
  return { from: rangeFrom, to: rangeTo }
}

export function dateRangeToStrings(range?: DateRange): [string, string] {
  return [toIsoDate(range?.from), toIsoDate(range?.to)]
}

type DateRangePickerProps = {
  value?: DateRange
  onChange: (value: DateRange | undefined) => void
  placeholder?: string
  className?: string
  numberOfMonths?: number
  popoverPortal?: boolean
  align?: "start" | "center" | "end"
}

export function DateRangePicker({
  value,
  onChange,
  placeholder = "Pick a date range",
  className,
  numberOfMonths = 2,
  popoverPortal = true,
  align = "start",
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false)

  const label = React.useMemo(() => {
    if (!value?.from) return placeholder
    if (value.to) {
      return `${format(value.from, DISPLAY_DATE_FORMAT)} – ${format(value.to, DISPLAY_DATE_FORMAT)}`
    }
    return format(value.from, DISPLAY_DATE_FORMAT)
  }, [placeholder, value])

  const hasValue = Boolean(value?.from)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          className={cn(
            fieldControlClassName,
            "flex items-center justify-between gap-2 whitespace-nowrap text-left font-normal",
            open && "ring-1 ring-ring",
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 truncate">
            <CalendarIcon className="h-4 w-4 shrink-0 opacity-70" />
            <span
              className={cn(
                "truncate",
                hasValue ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {label}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align={align}
        portal={popoverPortal}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Calendar
          initialFocus
          mode="range"
          defaultMonth={value?.from}
          selected={value}
          onSelect={onChange}
          numberOfMonths={numberOfMonths}
        />
      </PopoverContent>
    </Popover>
  )
}
